import { Share } from "@capacitor/share";
import { isNativePlatform } from "./platform";

/**
 * Trigger the OS native share sheet on iOS/Android.
 * Returns true if the share was attempted natively, false on web (caller should
 * fall back to `navigator.share` / a popover).
 */
export const nativeShare = async (opts: {
  title?: string;
  text?: string;
  url: string;
  dialogTitle?: string;
}): Promise<boolean> => {
  if (!isNativePlatform()) return false;
  try {
    await Share.share({
      title: opts.title,
      text: opts.text,
      url: opts.url,
      dialogTitle: opts.dialogTitle ?? "Share",
    });
    return true;
  } catch {
    // user cancelled or plugin unavailable
    return true;
  }
};

/**
 * On native, prefer the installed target app for WhatsApp links by using its
 * own URL scheme (wa.me always resolves to the website in a browser).
 */
const nativeTargetUrl = (url: string): string => {
  const waMatch = url.match(/^https:\/\/wa\.me\/\?text=(.*)$/);
  if (waMatch) return `whatsapp://send?text=${waMatch[1]}`;
  return url;
};

/**
 * Open an external URL. On native we hand the URL to the OS (ACTION_VIEW on
 * Android / UIApplication.open on iOS) so installed apps such as Pinterest,
 * X, Facebook or WhatsApp handle it directly; we only fall back to the system
 * browser when no app can. On web, opens a standard popup.
 */
export const openExternalUrl = async (url: string): Promise<void> => {
  if (isNativePlatform()) {
    const target = nativeTargetUrl(url);
    try {
      const { AppLauncher } = await import("@capacitor/app-launcher");
      const { value: canOpen } = await AppLauncher.canOpenUrl({ url: target });
      if (canOpen) {
        await AppLauncher.openUrl({ url: target });
        return;
      }
      // Target app not installed — retry the original https link, which the OS
      // may still route to another installed handler.
      if (target !== url) {
        const { value: canOpenWeb } = await AppLauncher.canOpenUrl({ url });
        if (canOpenWeb) {
          await AppLauncher.openUrl({ url });
          return;
        }
      }
    } catch (err) {
      console.warn("[openExternalUrl] AppLauncher unavailable, falling back", err);
    }

    try {
      const { InAppBrowser } = await import("@capacitor/inappbrowser");
      await InAppBrowser.openInSystemBrowser({
        url,
        // The plugin requires an options object; defaults are fine.
        options: {} as any,
      });
      return;
    } catch (err) {
      console.error("[openExternalUrl] failed to open system browser", err);
      return;
    }
  }
  window.open(url, "_blank", "noopener,noreferrer,width=600,height=500");
};

/**
 * Copy text to clipboard with a fallback for restrictive WebView contexts.
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy path
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};
